<template>
  <div
    class="labelling-shell"
    :style="{
      marginTop: headerHeight + 'px',
      minHeight: 'calc(100vh - ' + headerHeight + 'px)'
    }"
  >
    <div class="labelling-sidebar" :style="{ top: headerHeight + 'px' }">
      <el-menu
        class="labelling-menu"
        :default-active="activeView"
        background-color="transparent"
        @select="handleViewSelect"
      >
        <el-menu-item v-if="canAccessReview" index="review">
          <i class="el-icon-view"></i>
          <span slot="title">Review</span>
        </el-menu-item>
        <el-menu-item v-if="canAccessAudit" index="audit">
          <i class="el-icon-document-checked"></i>
          <span slot="title">Audit</span>
        </el-menu-item>
        <el-menu-item v-if="canAccessTestModel" index="test-model">
          <i class="el-icon-cpu"></i>
          <span slot="title">Test Model</span>
        </el-menu-item>
      </el-menu>
    </div>

    <div class="labelling-main">
      <div class="labelling-content">
        <AppBreadcrumb
          root="Labelling Tool"
          :current="activeViewTitle"
          @root-click="handleBreadcrumbRootClick"
        >
          <el-button size="small" type="primary" plain @click="openGuideDialog">
            Guide & Instructions
          </el-button>
        </AppBreadcrumb>

        <LabellingGuideDialog
          :visible.sync="guideDialogVisible"
          :sensor-options="sensorOptions"
        />

        <div v-if="activeView === 'review'" class="review-screen">
          <div v-if="currentCycle" class="review-layout">
            <LabellingChartPanel
              :current-cycle="currentCycle"
              :chart-readings="chartReadings"
              :loading="chartLoading"
              :regions="regions"
              :reason-codes="reasonCodes"
              :quick-reason-region="quickReasonRegion"
              :region-overlay-labels-fn="regionOverlayLabels"
              :sensor-options="sensorOptions"
              @select-range="handleChartRangeSelection"
              @activate-region="activeRegionId = $event"
              @remove-region="removeRegion"
              @update-region-range="updateRegionRange"
              @update-region-sensor="updateRegionSensor"
              @toggle-region-reason="toggleRegionReason"
              @update-region-note="updateRegionNote"
              @close-quick-reason-picker="closeQuickReasonPicker"
            />

            <ReviewDecisionPanel
              :review-notes.sync="reviewNotes"
              :clean-cycle-confirmed.sync="cleanCycleConfirmed"
              :regions="regions"
              :timestamp-cycle="currentCycle"
              :format-region-timestamp-fn="formatRegionTimestamp"
              :format-region-range-fn="formatCurrentRegionRange"
              :format-reason-code-fn="formatReasonCode"
              @remove-region="removeRegion"
              @submit-review="submitReview"
            />
          </div>

          <div v-else class="empty-panel">
            No unreviewed cycles are available right now.
          </div>
        </div>

        <div v-if="activeView === 'audit'" class="audit-screen">
          <el-card shadow="never" class="panel-card">
            <div slot="header" class="clearfix">
              <div class="audit-card-header">
                <div class="audit-card-title-group">
                  <el-button
                    v-if="auditCycle"
                    icon="el-icon-arrow-left"
                    size="mini"
                    type="text"
                    @click="closeAuditCycle"
                  >
                    Back
                  </el-button>
                  <span>Cycle audit</span>
                </div>

                <el-radio-group
                  v-if="!auditCycle"
                  v-model="auditFilter"
                  size="mini"
                  @change="handleAuditFilterChange"
                >
                  <el-radio-button label="all">All</el-radio-button>
                  <el-radio-button label="reviewed">Reviewed</el-radio-button>
                  <el-radio-button label="unreviewed"
                    >Unreviewed</el-radio-button
                  >
                  <el-radio-button label="badRegions">
                    Bad Regions only
                  </el-radio-button>
                </el-radio-group>
              </div>
            </div>

            <div v-if="!auditCycle" class="audit-list-view">
              <div class="audit-table-shell">
                <el-table
                  v-loading="auditCyclesLoading"
                  :data="auditCycles"
                  :height="'100%'"
                  border
                  size="small"
                >
                  <el-table-column
                    prop="id"
                    label="Cycle ID"
                    min-width="120"
                  ></el-table-column>
                  <el-table-column
                    prop="deviceId"
                    label="Device ID"
                    min-width="120"
                  ></el-table-column>
                  <el-table-column
                    prop="durationLabel"
                    label="Duration"
                    min-width="100"
                  ></el-table-column>
                  <el-table-column
                    prop="reviewCount"
                    label="Review Count"
                    min-width="110"
                  ></el-table-column>
                  <el-table-column
                    prop="badRegionCount"
                    label="Bad Regions"
                    min-width="110"
                  ></el-table-column>
                  <el-table-column
                    prop="reviewedAt"
                    label="Review At"
                    min-width="140"
                  ></el-table-column>
                  <el-table-column
                    prop="reviewedBy"
                    label="Review By"
                    min-width="120"
                  ></el-table-column>
                  <el-table-column
                    prop="startedAt"
                    label="Started"
                    min-width="140"
                  ></el-table-column>
                  <el-table-column label="Status" min-width="110">
                    <template slot-scope="scope">
                      <el-tag
                        size="mini"
                        :type="
                          scope.row.reviewStatus === 'reviewed'
                            ? 'success'
                            : 'info'
                        "
                      >
                        {{ scope.row.reviewStatus }}
                      </el-tag>
                    </template>
                  </el-table-column>
                  <el-table-column label="Action" width="110">
                    <template slot-scope="scope">
                      <el-button
                        size="small"
                        type="primary"
                        plain
                        @click="openAuditCycle(scope.row)"
                      >
                        View
                      </el-button>
                    </template>
                  </el-table-column>
                </el-table>
              </div>

              <div class="audit-pagination">
                <el-pagination
                  :current-page="auditPage"
                  :page-size="auditPageSize"
                  :page-sizes="auditPageSizes"
                  :total="auditTotalCount"
                  background
                  layout="total, sizes, prev, pager, next"
                  @current-change="handleAuditPageChange"
                  @size-change="handleAuditPageSizeChange"
                ></el-pagination>
              </div>
            </div>

            <div v-else class="audit-detail-view">
              <div class="audit-dialog-body">
                <LabellingChartPanel
                  class="mb-10"
                  :current-cycle="auditCycle"
                  :chart-readings="auditChartReadings"
                  :loading="auditChartLoading"
                  :regions="auditRegions"
                  :reason-codes="reasonCodes"
                  :quick-reason-region="null"
                  :region-overlay-labels-fn="regionOverlayLabels"
                  :read-only="true"
                />

                <div
                  v-if="!auditChartReadings.length"
                  class="chart-empty-state mb-20"
                >
                  No capacitance readings available for this cycle.
                </div>

                <div v-if="auditReviews.length" class="review-summary">
                  <div class="detail-label mb-10">Submitted reviews</div>
                  <div class="review-list">
                    <div
                      v-for="(review, reviewIndex) in auditReviews"
                      :key="review.id || 'audit-review-' + reviewIndex"
                      class="review-card"
                    >
                      <div class="review-card-header">
                        <div class="review-card-heading">
                          <span
                            class="reviewer-marker"
                            :style="{
                              backgroundColor: getAuditReviewColor(reviewIndex)
                            }"
                          ></span>
                          <strong>{{ review.reviewerName }}</strong>
                        </div>
                        <div class="review-card-meta">
                          <span>Submitted: </span>
                          <strong>{{ review.submittedAt }}</strong>
                        </div>
                      </div>

                      <div v-if="review.notes" class="region-note mt-6">
                        {{ review.notes }}
                      </div>

                      <div
                        v-if="review.regions.length"
                        class="region-list mt-10"
                      >
                        <RegionSummaryCard
                          v-for="region in review.regions"
                          :key="review.id + '-' + region.id"
                          :region="region"
                          :format-region-range-fn="formatCurrentRegionRange"
                          :timestamp-cycle="auditCycle"
                          :format-region-timestamp-fn="formatRegionTimestamp"
                          :format-reason-code-fn="formatReasonCode"
                          :show-sensor-tag="true"
                          :show-sensor-row="false"
                          :show-timestamp-header="true"
                          :show-timestamp-row="false"
                        />
                      </div>

                      <div v-else class="empty-inline mt-6">
                        No regions were selected in this review.
                      </div>
                    </div>
                  </div>
                </div>

                <div v-else class="empty-inline">
                  This cycle has not been reviewed yet.
                </div>
              </div>
            </div>
          </el-card>
        </div>

        <div v-if="activeView === 'test-model'" class="test-model-screen">
          <el-card shadow="never" class="panel-card">
            <div slot="header" class="audit-card-header">
              <div class="audit-card-title-group">
                <span>Test Model</span>
              </div>
            </div>

            <div class="test-model-layout">
              <div class="test-model-toolbar">
                <el-input
                  v-model.trim="testModelCycleId"
                  size="small"
                  clearable
                  placeholder="Enter pad cycle id"
                  @keyup.enter.native="loadTestModelPrediction"
                >
                  <template slot="prepend">
                    Pad Cycle ID
                  </template>
                </el-input>

                <el-button
                  type="primary"
                  size="small"
                  :loading="testModelLoading"
                  @click="loadTestModelPrediction"
                >
                  Run Prediction
                </el-button>
              </div>

              <div v-if="testModelCycle" class="test-model-results">
                <div class="test-model-summary">
                  <el-tag size="mini">Blocks {{ testModelBlockCount }}</el-tag>
                  <el-tag size="mini" type="success">
                    GREEN {{ testModelGreenBlockCount }}
                  </el-tag>
                  <el-tag size="mini" type="danger">
                    RED {{ testModelRedBlockCount }}
                  </el-tag>
                </div>

                <LabellingChartPanel
                  :current-cycle="testModelCycle"
                  :chart-readings="testModelChartReadings"
                  :loading="testModelLoading || testModelChartLoading"
                  :regions="[]"
                  :reason-codes="[]"
                  :hovered-block="testModelHoveredBlock"
                  :block-info-anchor="testModelBlockInfoAnchor"
                  :quick-reason-region="null"
                  :region-overlay-labels-fn="getEmptyRegionLabels"
                  :sensor-options="[]"
                  block-info-trigger="click"
                  :read-only="true"
                  :block-overlay-style-fn="getPredictionBlockOverlayStyle"
                  @chart-hover="handleTestModelChartHover"
                  @chart-leave="clearTestModelChartHover"
                  @close-block-info="clearTestModelChartHover"
                />

                <div
                  v-if="!testModelChartReadings.length"
                  class="chart-empty-state"
                >
                  No capacitance readings available for this cycle.
                </div>
              </div>

              <div v-else class="empty-panel test-model-empty-state">
                Enter a pad cycle id to run the model and inspect block
                predictions.
              </div>
            </div>
          </el-card>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LabellingChartPanel from '@/components/labelling/LabellingChartPanel'
import LabellingGuideDialog from '@/components/labelling/LabellingGuideDialog'
import CommonApiService from '@/services/CommonApiService'
import LabellingToolApiService from '@/services/labelling/LabellingToolApiService'
import moment from 'moment-timezone'
import RegionSummaryCard from '@/components/labelling/RegionSummaryCard'
import ReviewDecisionPanel from '@/components/labelling/ReviewDecisionPanel'
import headerHeight from '@/mixins/headerHeight'
import AppBreadcrumb from '@/components/AppBreadcrumb'

const LABELLING_VIEW_PERMISSION = 'Data Entry.Labelling Tool.View'
const LABELLING_AUDIT_PERMISSION = 'Data Entry.Labelling Tool.Audit'
const LABELLING_TEST_PERMISSION = 'Data Entry.Labelling Tool.Test'
const VIEW_TITLES = {
  review: 'Review',
  audit: 'Audit',
  'test-model': 'Test Model'
}
const AUDIT_REVIEW_COLORS = [
  '#1d4ed8',
  '#0f766e',
  '#7c3aed',
  '#b45309',
  '#be185d',
  '#0369a1'
]

const SENSOR_OPTIONS = [
  {
    value: 'S1',
    label: 'S1',
    fill: 'rgba(0, 143, 251, 0.24)',
    border: '#008ffb'
  },
  {
    value: 'S2',
    label: 'S2',
    fill: 'rgba(0, 227, 150, 0.24)',
    border: '#00e396'
  },
  {
    value: 'BOTH',
    label: 'BOTH',
    fill: 'rgba(245, 158, 11, 0.24)',
    border: '#f59e0b'
  }
]

export default {
  components: {
    LabellingChartPanel,
    LabellingGuideDialog,
    RegionSummaryCard,
    ReviewDecisionPanel,
    AppBreadcrumb
  },
  mixins: [headerHeight],
  data() {
    return {
      activeView: 'review',
      guideDialogVisible: false,
      reasonCodes: [],
      currentCycle: null,
      reviewNotes: '',
      cleanCycleConfirmed: false,
      chartReadings: [],
      chartLoading: false,
      auditChartReadings: [],
      auditChartLoading: false,
      testModelCycleId: '',
      testModelCycle: null,
      testModelLoading: false,
      testModelChartReadings: [],
      testModelChartLoading: false,
      testModelHoveredBlock: null,
      testModelBlockInfoAnchor: null,
      activeRegionId: null,
      regions: [],
      auditFilter: 'all',
      auditCyclesLoading: false,
      auditCycles: [],
      auditPage: 1,
      auditPageSize: 20,
      auditPageSizes: [20, 50, 100],
      auditTotalCount: 0,
      auditCycle: null,
      auditReviews: []
    }
  },
  computed: {
    currentUser() {
      const user = localStorage.getItem('user')
      return user ? JSON.parse(user) : null
    },
    currentPermissions() {
      const permissions = localStorage.getItem('permissions')
      return permissions ? JSON.parse(permissions) : []
    },
    reviewerId() {
      return this.currentUser && this.currentUser.id ? this.currentUser.id : ''
    },
    canAccessReview() {
      return this.currentPermissions.indexOf(LABELLING_VIEW_PERMISSION) > -1
    },
    canAccessAudit() {
      return this.currentPermissions.indexOf(LABELLING_AUDIT_PERMISSION) > -1
    },
    canAccessTestModel() {
      return this.currentPermissions.indexOf(LABELLING_TEST_PERMISSION) > -1
    },
    availableViews() {
      const views = []
      if (this.canAccessReview) {
        views.push({ value: 'review', label: 'Review' })
      }
      if (this.canAccessAudit) {
        views.push({ value: 'audit', label: 'Audit' })
      }
      if (this.canAccessTestModel) {
        views.push({ value: 'test-model', label: 'Test Model' })
      }
      return views
    },
    activeViewTitle() {
      return VIEW_TITLES[this.activeView] || ''
    },
    sensorOptions() {
      return SENSOR_OPTIONS
    },
    auditRegions() {
      if (!this.auditReviews.length) {
        return []
      }
      return this.auditReviews.reduce((regions, review, reviewIndex) => {
        const reviewColor = this.getAuditReviewColor(reviewIndex)
        const overlayLabelPrefix =
          review.reviewerName + (review.label ? ' (' + review.label + ')' : '')
        return regions.concat(
          (review.regions || []).map((region, regionIndex) => {
            const sensorOption =
              this.getSensorOption(region.sensor) ||
              this.getSensorOption('BOTH')
            return {
              ...region,
              id:
                (review.id || 'audit-review-' + reviewIndex) +
                '-region-' +
                (region.id || regionIndex),
              overlayColor: sensorOption.fill,
              overlayBorderColor: sensorOption.border,
              overlayLabels: [
                {
                  text: overlayLabelPrefix,
                  markerColor: reviewColor
                },
                this.formatSensorLabel(region.sensor)
              ].concat(
                (region.reasonCodes || []).map(code =>
                  this.formatReasonCode(code)
                )
              )
            }
          })
        )
      }, [])
    },
    quickReasonRegion() {
      if (!this.activeRegionId) {
        return null
      }
      return (
        this.regions.find(region => region.id === this.activeRegionId) || null
      )
    },
    testModelBlockCount() {
      return this.testModelCycle && Array.isArray(this.testModelCycle.blocks)
        ? this.testModelCycle.blocks.length
        : 0
    },
    testModelGreenBlockCount() {
      return this.countPredictionBlocks('GREEN')
    },
    testModelRedBlockCount() {
      return this.countPredictionBlocks('RED')
    }
  },
  mounted() {
    this.activeView = this.availableViews.length
      ? this.availableViews[0].value
      : 'review'
    this.guideDialogVisible = true
    this.loadBootstrap()
  },
  methods: {
    openGuideDialog() {
      this.guideDialogVisible = true
    },
    handleViewSelect(index) {
      this.activeView = index
    },
    handleBreadcrumbRootClick() {
      if (this.availableViews.length) {
        this.activeView = this.availableViews[0].value
      }
    },
    loadBootstrap() {
      LabellingToolApiService.GetReasonCodes()
        .then(reasonCodesRes => {
          this.reasonCodes = reasonCodesRes.data.data
          if (this.canAccessAudit) {
            this.loadAuditCycles()
          }
          if (this.canAccessReview) {
            this.loadNextCycle()
          }
        })
        .catch(error => {
          this.$message.error(
            this.resolveErrorMessage(error, 'Failed to load reason codes')
          )
        })
    },
    loadNextCycle() {
      this.resetReviewState()
      LabellingToolApiService.GetNextCycle(this.reviewerId)
        .then(response => {
          this.currentCycle = response.data.data
          if (!this.currentCycle) {
            return
          }
          this.reviewNotes = ''
          this.loadCurrentCycleChartData()
        })
        .catch(error => {
          this.$message.error(
            this.resolveErrorMessage(error, 'Failed to load the next cycle')
          )
        })
    },
    resetReviewState() {
      this.currentCycle = null
      this.reviewNotes = ''
      this.cleanCycleConfirmed = false
      this.chartReadings = []
      this.activeRegionId = null
      this.regions = []
      this.nextRegionSequence = 1
    },
    loadCurrentCycleChartData() {
      return this.loadCycleChartData(this.currentCycle, 'chartReadings')
    },
    loadCycleChartData(cycle, targetKey) {
      const request = this.getChartReadingsRequest(cycle)
      const loadingKeyMap = {
        chartReadings: 'chartLoading',
        auditChartReadings: 'auditChartLoading',
        testModelChartReadings: 'testModelChartLoading'
      }
      const loadingKey = loadingKeyMap[targetKey] || 'chartLoading'
      const ownerMatchesMap = {
        chartReadings: () =>
          !!this.currentCycle &&
          this.currentCycle.id === (cycle ? cycle.id : ''),
        auditChartReadings: () =>
          !!this.auditCycle && this.auditCycle.id === (cycle ? cycle.id : ''),
        testModelChartReadings: () =>
          !!this.testModelCycle &&
          this.testModelCycle.id === (cycle ? cycle.id : '')
      }
      const ownerMatches =
        ownerMatchesMap[targetKey] || ownerMatchesMap.chartReadings
      if (!request) {
        this[loadingKey] = false
        this[targetKey] = []
        return Promise.resolve()
      }
      this[loadingKey] = true
      return CommonApiService.GetCapacitanceReadings(
        request.macFilter,
        request.startTime,
        request.endTime,
        request.inputUtcOffset,
        true
      )
        .then(response => {
          if (!ownerMatches()) {
            return
          }
          this[targetKey] = this.sortChartReadings(
            this.unwrapCapacitanceReadings(response)
              .map(item => this.normalizeChartReading(item, request.startTime))
              .filter(item => item)
          )
        })
        .catch(() => {
          if (!ownerMatches()) {
            return
          }
          this[targetKey] = []
        })
        .finally(() => {
          if (!ownerMatches()) {
            return
          }
          this[loadingKey] = false
        })
    },
    getChartReadingsRequest(cycle) {
      if (!cycle || !cycle.startDate) {
        return null
      }
      const macFilter = cycle.podId || ''
      if (!macFilter || macFilter === '--') {
        return null
      }
      const startTime = cycle.startDate
      const startMs = this.parseUtcTimestamp(startTime)
      if (Number.isNaN(startMs)) {
        return null
      }
      const endTime =
        cycle.endDate ||
        new Date(startMs + (cycle.durationSec || 0) * 1000).toISOString()
      return {
        macFilter,
        startTime,
        endTime,
        inputUtcOffset: 0
      }
    },
    unwrapCapacitanceReadings(response) {
      const readings =
        response && response.data && response.data.data
          ? response.data.data.data
          : null
      return Array.isArray(readings) ? readings : []
    },
    normalizeChartReading(reading, cycleStartDate) {
      if (!reading || !reading.timeStamp || !cycleStartDate) {
        return null
      }
      const readingTime = this.parseUtcTimestamp(reading.timeStamp)
      const cycleStart = this.parseUtcTimestamp(cycleStartDate)
      if (Number.isNaN(readingTime) || Number.isNaN(cycleStart)) {
        return null
      }
      return {
        timeStamp: reading.timeStamp,
        sensor1Value: Number(reading.sensor1Value) || 0,
        sensor2Value: Number(reading.sensor2Value) || 0
      }
    },
    sortChartReadings(readings) {
      return [...readings].sort((left, right) => {
        const leftTime = this.parseUtcTimestamp(left.timeStamp)
        const rightTime = this.parseUtcTimestamp(right.timeStamp)
        if (Number.isNaN(leftTime) || Number.isNaN(rightTime)) {
          return 0
        }
        return leftTime - rightTime
      })
    },
    loadAuditCycles() {
      if (!this.canAccessAudit) {
        this.auditCyclesLoading = false
        this.auditCycles = []
        this.auditTotalCount = 0
        return
      }
      this.auditCyclesLoading = true
      LabellingToolApiService.GetCycles(
        this.auditFilter === 'badRegions' ? 'all' : this.auditFilter,
        this.auditFilter === 'badRegions' ? true : null,
        this.reviewerId,
        this.auditPage,
        this.auditPageSize
      )
        .then(response => {
          const payload = response.data.data
          this.auditCycles = payload.items
          this.auditTotalCount = payload.totalCount
          this.auditPage = payload.page
          this.auditPageSize = payload.pageSize
        })
        .catch(error => {
          this.$message.error(
            this.resolveErrorMessage(error, 'Failed to load audit cycles')
          )
        })
        .finally(() => {
          this.auditCyclesLoading = false
        })
    },
    handleAuditFilterChange() {
      this.auditPage = 1
      this.loadAuditCycles()
    },
    handleAuditPageChange(page) {
      this.auditPage = page
      this.loadAuditCycles()
    },
    handleAuditPageSizeChange(pageSize) {
      this.auditPage = 1
      this.auditPageSize = pageSize
      this.loadAuditCycles()
    },
    addRegion(range) {
      if (!range) {
        return false
      }
      const normalizedRange = this.normalizeRegionRange(
        range.startSec !== undefined ? range.startSec : range[0],
        range.endSec !== undefined ? range.endSec : range[1]
      )
      if (!normalizedRange) {
        return false
      }
      const { startSec, endSec } = normalizedRange
      if (startSec === endSec) {
        return false
      }
      const existingRegion = this.regions.find(
        region => region.startSec === startSec && region.endSec === endSec
      )
      if (existingRegion) {
        this.activeRegionId = existingRegion.id
        this.$message.warning('That section already exists.')
        return null
      }
      if (this.hasOverlappingRegion(startSec, endSec)) {
        this.$message.warning(
          'Sections cannot overlap. Create the section in an open range.'
        )
        return null
      }
      const region = {
        id: 'REGION-' + this.nextRegionSequence,
        startSec,
        endSec,
        startTime:
          range.startTime ||
          this.getRegionTimestamp(this.currentCycle, startSec),
        endTime:
          range.endTime || this.getRegionTimestamp(this.currentCycle, endSec),
        sensor: '',
        reasonCodes: [],
        note: ''
      }
      this.applyRegionSensorAppearance(region)
      this.regions.push(region)
      this.nextRegionSequence += 1
      this.sortRegions()
      return region
    },
    removeRegion(regionIdentifier) {
      const regionIndex =
        typeof regionIdentifier === 'number'
          ? regionIdentifier
          : this.regions.findIndex(region => region.id === regionIdentifier)
      if (regionIndex < 0) {
        return
      }
      const [removedRegion] = this.regions.splice(regionIndex, 1)
      if (removedRegion && removedRegion.id === this.activeRegionId) {
        this.activeRegionId = null
      }
    },
    toggleRegionReason(regionId, code) {
      const region = this.regions.find(item => item.id === regionId)
      if (!region) {
        return
      }
      if (region.reasonCodes.indexOf(code) > -1) {
        region.reasonCodes = region.reasonCodes.filter(item => item !== code)
        return
      }
      region.reasonCodes = [...region.reasonCodes, code]
    },
    updateRegionNote(regionId, note) {
      const region = this.regions.find(item => item.id === regionId)
      if (!region) {
        return
      }
      region.note = note
    },
    updateRegionSensor(regionId, sensor) {
      const region = this.regions.find(item => item.id === regionId)
      if (!region) {
        return
      }
      region.sensor = sensor
      this.applyRegionSensorAppearance(region)
    },
    closeQuickReasonPicker() {
      this.activeRegionId = null
    },
    getSensorOption(sensor) {
      return SENSOR_OPTIONS.find(option => option.value === sensor) || null
    },
    applyRegionSensorAppearance(region) {
      if (!region) {
        return
      }
      const sensorOption = this.getSensorOption(region.sensor)
      if (!sensorOption) {
        region.sensor = ''
        region.overlayColor = 'rgba(148, 163, 184, 0.2)'
        region.overlayBorderColor = '#94a3b8'
        return
      }
      region.sensor = sensorOption.value
      region.overlayColor = sensorOption.fill
      region.overlayBorderColor = sensorOption.border
    },
    normalizeRegionRange(startSec, endSec, minWidth = 1) {
      const maxSec = this.getMaxRegionSecond()
      if (!Number.isFinite(maxSec) || maxSec <= 0) {
        return null
      }
      const numericStart = Number(startSec)
      const numericEnd = Number(endSec)
      if (!Number.isFinite(numericStart) || !Number.isFinite(numericEnd)) {
        return null
      }
      let normalizedStart = Math.round(Math.min(numericStart, numericEnd))
      let normalizedEnd = Math.round(Math.max(numericStart, numericEnd))
      normalizedStart = Math.max(normalizedStart, 0)
      normalizedEnd = Math.min(normalizedEnd, maxSec)
      if (normalizedEnd - normalizedStart < minWidth) {
        normalizedEnd = Math.min(normalizedStart + minWidth, maxSec)
      }
      if (normalizedEnd <= normalizedStart) {
        return null
      }
      return {
        startSec: normalizedStart,
        endSec: normalizedEnd
      }
    },
    getMaxRegionSecond() {
      const durations = []
      if (
        this.currentCycle &&
        Number.isFinite(Number(this.currentCycle.durationSec))
      ) {
        durations.push(Number(this.currentCycle.durationSec))
      }
      if (
        this.currentCycle &&
        this.currentCycle.startDate &&
        this.currentCycle.endDate
      ) {
        const startTime = this.parseUtcTimestamp(this.currentCycle.startDate)
        const endTime = this.parseUtcTimestamp(this.currentCycle.endDate)
        if (!Number.isNaN(startTime) && !Number.isNaN(endTime)) {
          durations.push(Math.round((endTime - startTime) / 1000))
        }
      }
      if (this.currentCycle && Array.isArray(this.currentCycle.blocks)) {
        this.currentCycle.blocks.forEach(block => {
          if (Number.isFinite(Number(block.endSec))) {
            durations.push(Number(block.endSec))
          }
        })
      }
      if (
        this.chartReadings.length &&
        this.currentCycle &&
        this.currentCycle.startDate
      ) {
        const cycleStart = this.parseUtcTimestamp(this.currentCycle.startDate)
        const lastReading = this.chartReadings[this.chartReadings.length - 1]
        const lastReadingTime = this.parseUtcTimestamp(lastReading.timeStamp)
        if (!Number.isNaN(cycleStart) && !Number.isNaN(lastReadingTime)) {
          durations.push(Math.round((lastReadingTime - cycleStart) / 1000))
        }
      }
      return durations.length ? Math.max(...durations, 0) : 0
    },
    sortRegions() {
      this.regions.sort((left, right) => {
        if (left.startSec === right.startSec) {
          return left.endSec - right.endSec
        }
        return left.startSec - right.startSec
      })
    },
    hasOverlappingRegion(startSec, endSec, excludedRegionId = null) {
      return this.regions.some(region => {
        if (excludedRegionId && region.id === excludedRegionId) {
          return false
        }
        return startSec < region.endSec && endSec > region.startSec
      })
    },
    clampValue(value, min, max) {
      if (max < min) {
        return min
      }
      return Math.min(Math.max(value, min), max)
    },
    resolveAdjustedRegionRange(region, nextRange, mode) {
      if (!region || !nextRange) {
        return null
      }
      const minWidth = 1
      const sortedRegions = [...this.regions].sort((left, right) => {
        if (left.startSec === right.startSec) {
          return left.endSec - right.endSec
        }
        return left.startSec - right.startSec
      })
      const currentIndex = sortedRegions.findIndex(
        item => item.id === region.id
      )
      const previousRegion =
        currentIndex > 0 ? sortedRegions[currentIndex - 1] : null
      const nextRegion =
        currentIndex > -1 && currentIndex < sortedRegions.length - 1
          ? sortedRegions[currentIndex + 1]
          : null
      const leftLimit = previousRegion ? previousRegion.endSec : 0
      const rightLimit = nextRegion
        ? nextRegion.startSec
        : this.getMaxRegionSecond()
      let startSec = nextRange.startSec
      let endSec = nextRange.endSec

      if (mode === 'move') {
        const width = Math.max(region.endSec - region.startSec, minWidth)
        startSec = this.clampValue(startSec, leftLimit, rightLimit - width)
        endSec = startSec + width
      } else if (mode === 'resize-left') {
        startSec = this.clampValue(
          startSec,
          leftLimit,
          region.endSec - minWidth
        )
        endSec = region.endSec
      } else if (mode === 'resize-right') {
        startSec = region.startSec
        endSec = this.clampValue(endSec, region.startSec + minWidth, rightLimit)
      }

      if (endSec <= startSec) {
        return null
      }
      return {
        startSec,
        endSec
      }
    },
    updateRegionRange(payload) {
      if (!payload || !payload.regionId) {
        return
      }
      const region = this.regions.find(item => item.id === payload.regionId)
      if (!region) {
        return
      }
      const normalizedRange = this.normalizeRegionRange(
        payload.startSec,
        payload.endSec
      )
      if (!normalizedRange) {
        return
      }
      const adjustedRange = this.resolveAdjustedRegionRange(
        region,
        normalizedRange,
        payload.mode
      )
      if (
        !adjustedRange ||
        this.hasOverlappingRegion(
          adjustedRange.startSec,
          adjustedRange.endSec,
          region.id
        )
      ) {
        return
      }
      region.startSec = adjustedRange.startSec
      region.endSec = adjustedRange.endSec
      region.startTime = this.getRegionTimestamp(
        this.currentCycle,
        adjustedRange.startSec
      )
      region.endTime = this.getRegionTimestamp(
        this.currentCycle,
        adjustedRange.endSec
      )
      this.sortRegions()
    },
    handleTestModelChartHover(event) {
      if (!event || event.second === undefined) {
        this.clearTestModelChartHover()
        return
      }
      if (
        !this.testModelCycle ||
        !this.testModelCycle.blocks ||
        !this.testModelCycle.blocks.length
      ) {
        this.clearTestModelChartHover()
        return
      }
      const block = this.getBlockAtSecond(event.second, this.testModelCycle)
      if (!block) {
        this.clearTestModelChartHover()
        return
      }
      this.testModelHoveredBlock = block
      this.testModelBlockInfoAnchor = {
        leftPx: event.leftPx,
        topPx: event.topPx,
        chartWidth: event.chartWidth,
        chartHeight: event.chartHeight
      }
    },
    clearTestModelChartHover() {
      this.testModelHoveredBlock = null
      this.testModelBlockInfoAnchor = null
    },
    closeAuditCycle() {
      this.auditCycle = null
      this.auditReviews = []
      this.auditChartReadings = []
    },
    loadTestModelPrediction() {
      if (!this.canAccessTestModel) {
        this.$message.error('You do not have permission to access Test Model.')
        return
      }
      if (!this.testModelCycleId) {
        this.$message.error('Please enter a pad cycle id.')
        return
      }
      this.testModelLoading = true
      this.testModelCycle = null
      this.testModelChartReadings = []
      this.clearTestModelChartHover()
      LabellingToolApiService.GetCyclePrediction(this.testModelCycleId)
        .then(response => {
          const cycle = response && response.data ? response.data.data : null
          if (!cycle || !cycle.id) {
            throw new Error('Prediction response is missing cycle details.')
          }
          this.testModelCycle = cycle
          return this.loadCycleChartData(cycle, 'testModelChartReadings')
        })
        .catch(error => {
          this.testModelCycle = null
          this.testModelChartReadings = []
          this.$message.error(
            this.resolveErrorMessage(error, 'Failed to load model prediction')
          )
        })
        .finally(() => {
          this.testModelLoading = false
        })
    },
    handleChartRangeSelection(range) {
      const region = this.addRegion(range)
      if (region) {
        this.activeRegionId = region.id
        this.$message.success('Region added. Pick a sensor and reason code.')
      }
    },
    getRegionTimestamp(cycle, second) {
      if (!cycle || !cycle.startDate) {
        return null
      }
      const cycleStart = this.parseUtcTimestamp(cycle.startDate)
      if (Number.isNaN(cycleStart)) {
        return null
      }
      return new Date(cycleStart + (Number(second) || 0) * 1000).toISOString()
    },
    formatRegionTimestamp(cycle, timestamp, second) {
      return this.formatChartTimestamp(
        timestamp || this.getRegionTimestamp(cycle, second)
      )
    },
    formatRelativeRegionLabel(second) {
      const totalMinutes = Math.max(Math.round((Number(second) || 0) / 60), 0)
      if (totalMinutes < 60) {
        return totalMinutes + ' min'
      }
      const hours = Math.floor(totalMinutes / 60)
      const minutes = totalMinutes % 60
      if (!minutes) {
        return hours + ' hr'
      }
      return hours + ' hr ' + minutes + ' min'
    },
    formatCurrentRegionRange(region) {
      return (
        this.formatRelativeRegionLabel(region.startSec) +
        ' - ' +
        this.formatRelativeRegionLabel(region.endSec)
      )
    },
    getBlockAtSecond(second, cycle = this.currentCycle) {
      if (!cycle || !cycle.blocks) {
        return null
      }
      return (
        cycle.blocks.find(
          block => second >= block.startSec && second <= block.endSec
        ) || null
      )
    },
    submitReview() {
      if (!this.currentCycle) {
        return
      }
      if (!this.regions.length && !this.cleanCycleConfirmed) {
        this.$message.error(
          'Please confirm the sensor values look clean throughout the pad cycle.'
        )
        return
      }
      const hasRegionWithoutReasonCode = this.regions.some(
        region =>
          !Array.isArray(region.reasonCodes) || region.reasonCodes.length === 0
      )
      if (hasRegionWithoutReasonCode) {
        this.$message.error('Each section must have at least one reason code.')
        return
      }
      const hasRegionWithoutSensor = this.regions.some(region => !region.sensor)
      if (hasRegionWithoutSensor) {
        this.$message.error('Each section must have a selected sensor.')
        return
      }
      const payload = {
        cycleLabel: this.regions.length ? 'BAD' : 'GOOD',
        notes: this.reviewNotes,
        sections: this.regions.map(region => ({
          sectionStart: region.startTime,
          sectionEnd: region.endTime,
          sensor: region.sensor,
          notes: region.note,
          reasonCodes: region.reasonCodes
        }))
      }
      LabellingToolApiService.SubmitReview(this.currentCycle.id, payload)
        .then(response => {
          if (!response.data.success) {
            this.$message.error(response.data.msg || 'Failed to save review')
            return
          }
          this.$message.success('Review saved. Loading the next cycle.')
          if (this.canAccessAudit) {
            this.loadAuditCycles()
          }
          this.loadNextCycle()
        })
        .catch(error => {
          this.$message.error(
            this.resolveErrorMessage(error, 'Failed to submit the review')
          )
        })
    },
    openAuditCycle(cycle) {
      this.auditCycle = null
      this.auditReviews = []
      this.auditChartReadings = []
      LabellingToolApiService.GetCycleById(cycle.id, this.reviewerId)
        .then(cycleRes => {
          this.auditCycle = cycleRes.data.data
          return Promise.all([
            LabellingToolApiService.GetCycleReviews(
              this.auditCycle.id,
              this.auditCycle.startDate,
              this.reviewerId
            ),
            this.loadCycleChartData(this.auditCycle, 'auditChartReadings')
          ])
        })
        .then(result => {
          if (!result) {
            return
          }
          const [reviewRes] = result
          if (reviewRes) {
            this.auditReviews = reviewRes.data.data || []
          }
        })
        .catch(error => {
          this.$message.error(
            this.resolveErrorMessage(error, 'Failed to load cycle reviews')
          )
        })
    },
    formatChartTimestamp(value) {
      if (!value) {
        return '--'
      }
      const timestamp = this.parseUtcTimestamp(value)
      if (Number.isNaN(timestamp)) {
        return value
      }
      const formatted = moment.utc(timestamp).format('YYYY-MM-DD HH:mm:ss')
      return formatted === 'Invalid date' ? value : formatted
    },
    parseUtcTimestamp(value) {
      if (value === null || value === undefined || value === '') {
        return NaN
      }
      const utcTime = moment.utc(value).valueOf()
      if (Number.isFinite(utcTime)) {
        return utcTime
      }
      const nativeTime = new Date(value).getTime()
      return Number.isFinite(nativeTime) ? nativeTime : NaN
    },
    formatReasonCode(code) {
      const reason = this.reasonCodes.find(item => item.code === code)
      return reason ? reason.label : code
    },
    formatSensorLabel(sensor) {
      const sensorOption = this.getSensorOption(sensor)
      return sensorOption ? sensorOption.label : 'Select sensor'
    },
    getAuditReviewColor(index) {
      return AUDIT_REVIEW_COLORS[index % AUDIT_REVIEW_COLORS.length]
    },
    countPredictionBlocks(prediction) {
      if (!this.testModelCycle || !Array.isArray(this.testModelCycle.blocks)) {
        return 0
      }
      return this.testModelCycle.blocks.filter(
        block => String(block.prediction || '').toUpperCase() === prediction
      ).length
    },
    getPredictionBlockOverlayStyle(block) {
      const prediction = String(block.prediction || '').toUpperCase()
      if (prediction === 'GREEN') {
        return {
          background: 'rgba(34, 197, 94, 0.18)',
          borderRight: '1px solid rgba(34, 197, 94, 0.45)'
        }
      }
      if (prediction === 'RED') {
        return {
          background: 'rgba(239, 68, 68, 0.18)',
          borderRight: '1px solid rgba(239, 68, 68, 0.45)'
        }
      }
      return {}
    },
    getEmptyRegionLabels() {
      return []
    },
    regionOverlayLabels(region) {
      if (region.overlayLabels && region.overlayLabels.length) {
        return region.overlayLabels
      }
      const labels = [this.formatSensorLabel(region.sensor)]
      if (!region.reasonCodes || !region.reasonCodes.length) {
        labels.push('Select reason')
        return labels
      }
      return labels.concat(
        region.reasonCodes.map(code => this.formatReasonCode(code))
      )
    },
    resolveErrorMessage(error, fallbackMessage) {
      if (error && error.response && error.response.data) {
        return (
          error.response.data.msg ||
          error.response.data.message ||
          fallbackMessage
        )
      }
      return error && error.message ? error.message : fallbackMessage
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/css/theme.scss';

// Not position:fixed — see the identical note in Lora.vue's .lora-shell:
// a fixed ancestor would trap .labelling-sidebar's stacking context and
// lose to the global footer bar regardless of z-index.
.labelling-shell {
  display: flex;
  background: var(--bg3);
  font-size: 14px;
  font-family: var(--font);
}

.labelling-sidebar {
  position: fixed;
  left: 0;
  bottom: 0;
  width: 210px;
  z-index: 10;
  background: var(--sidebar);
  overflow-y: auto;
  padding-top: 20px;
  padding-bottom: 16px;

  /deep/ .el-menu {
    border-right: none;
  }

  /deep/ .el-menu-item {
    height: 36px;
    line-height: 36px;
    padding: 0 14px !important;
    margin: 1px 6px;
    width: calc(100% - 12px);
    font-size: 13px;
    color: var(--sidebar-t2);
    border-radius: 6px;

    i {
      color: inherit;
      font-size: 16px;
      margin-right: 9px;
      width: auto;
    }

    &:hover,
    &:focus {
      background-color: var(--sidebar2) !important;
      color: var(--sidebar-t);
    }
  }

  /deep/ .el-menu-item.is-active {
    background-color: var(--sidebar-a) !important;
    color: #fff;
  }
}

.labelling-main {
  margin-left: 210px;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.labelling-content {
  flex: 1;
  padding: 12px 24px 16px;
  width: 100%;
  margin: 0 auto;

  /deep/ .app-breadcrumb {
    margin-bottom: 10px;
  }

  .panel-card,
  .empty-panel {
    border: 1px solid var(--border);
    border-radius: 14px;
    background: var(--bg);
  }

  .review-layout {
    display: grid;
    gap: 10px;
  }

  .empty-panel {
    padding: 48px 20px;
    text-align: center;
    color: var(--text2);
  }

  .detail-label {
    font-size: 12px;
    color: var(--text2);
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .empty-inline,
  .region-note {
    color: var(--text2);
    line-height: 1.6;
  }

  .review-list {
    display: grid;
    gap: 10px;
  }

  .review-card {
    padding: 12px;
    border: 1px solid var(--border);
    border-radius: 12px;
    background: var(--bg2);
  }

  .review-card-header,
  .review-card-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }

  .review-card-heading {
    justify-content: flex-start;
  }

  .reviewer-marker {
    width: 10px;
    height: 10px;
    border-radius: 999px;
    flex: 0 0 10px;
  }

  .region-list {
    display: grid;
    gap: 8px;
  }

  .region-title-group {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
  }

  .review-card-meta {
    font-size: 12px;
  }

  .action-row {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
  }

  .audit-card-title-group {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .audit-card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
  }

  .audit-list-view {
    display: grid;
    grid-template-rows: minmax(0, 1fr) auto;
    gap: 12px;
    height: calc(100vh - 250px);
    min-height: 420px;
  }

  .audit-table-shell {
    min-height: 0;
    overflow: hidden;
  }

  .audit-pagination {
    display: flex;
    justify-content: flex-end;
  }

  .audit-dialog-body {
    display: grid;
    gap: 10px;
  }

  .test-model-layout {
    display: grid;
    gap: 10px;
  }

  .test-model-toolbar {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 12px;
    align-items: center;
  }

  .test-model-results {
    display: grid;
    gap: 14px;
  }

  .test-model-summary {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .test-model-empty-state {
    padding: 32px 20px;
  }
}

@media (max-width: 767px) {
  .labelling-content {
    .audit-card-header {
      flex-direction: column;
      align-items: stretch;
    }

    .audit-list-view {
      height: calc(100vh - 290px);
      min-height: 360px;
    }

    .audit-card-title-group {
      justify-content: flex-start;
    }

    .audit-pagination {
      overflow-x: auto;
    }

    .test-model-toolbar {
      grid-template-columns: 1fr;
    }
  }
}
</style>
